import { useState, useEffect, useReducer, useContext } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_WHATSAPPS") {
    const whatsApps = action.payload;
    return [...whatsApps];
  }

  if (action.type === "UPDATE_WHATSAPPS") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex] = whatsApp;
      return [...state];
    } else {
      return [whatsApp, ...state];
    }
  }

  if (action.type === "UPDATE_SESSION") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex].status = whatsApp.status;
      state[whatsAppIndex].updatedAt = whatsApp.updatedAt;
      state[whatsAppIndex].qrcode = whatsApp.qrcode;
      state[whatsAppIndex].retries = whatsApp.retries;
      return [...state];
    } else {
      return [...state];
    }
  }

  if (action.type === "DELETE_WHATSAPPS") {
    const whatsAppId = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsAppId);
    if (whatsAppIndex !== -1) {
      state.splice(whatsAppIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Cache para evitar requisições desnecessárias
let whatsAppsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

const useWhatsApps = () => {
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const { user, socket } = useContext(AuthContext);

  // Effect para carregar os dados iniciais
  useEffect(() => {
    // IMPORTANTE: Só buscar WhatsApps se o usuário estiver autenticado
    if (!user || !user.companyId) {
      setLoading(false);
      return;
    }

    const now = Date.now();

    // Verificar se há cache válido
    if (whatsAppsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      dispatch({ type: "LOAD_WHATSAPPS", payload: whatsAppsCache });
      setLoading(false);
      return;
    }

    // Só buscar se não há cache válido E usuário está autenticado
    let isMounted = true;
    setLoading(true);
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/?session=0");

        // Atualizar cache
        whatsAppsCache = data;
        cacheTimestamp = now;

        if (isMounted) {
          dispatch({ type: "LOAD_WHATSAPPS", payload: data });
          setLoading(false);
        }
      } catch (err) {
        console.error("Erro ao carregar WhatsApps:", err);
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchSession();
    return () => {
      isMounted = false;
    };
  }, [user]); // Adicionar user como dependência para reagir à autenticação

  // Effect para configurar os listeners do socket
  useEffect(() => {
    if (user?.companyId && socket && typeof socket.on === 'function' && typeof socket.off === 'function') {
      const companyId = user.companyId;

      const onCompanyWhatsapp = (data) => {
        if (data.action === "update") {
          dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
          // Atualizar cache quando houver mudanças
          if (whatsAppsCache) {
            const index = whatsAppsCache.findIndex(w => w.id === data.whatsapp.id);
            if (index !== -1) {
              whatsAppsCache[index] = data.whatsapp;
            } else {
              whatsAppsCache.push(data.whatsapp);
            }
          }
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
          // Atualizar cache quando houver mudanças
          if (whatsAppsCache) {
            whatsAppsCache = whatsAppsCache.filter(w => w.id !== data.whatsappId);
          }
        }
      };

      const onCompanyWhatsappSession = (data) => {
        if (data.action === "update") {
          dispatch({ type: "UPDATE_SESSION", payload: data.session });
          // Atualizar cache quando houver mudanças
          if (whatsAppsCache) {
            const index = whatsAppsCache.findIndex(w => w.id === data.session.id);
            if (index !== -1) {
              whatsAppsCache[index].status = data.session.status;
              whatsAppsCache[index].updatedAt = data.session.updatedAt;
              whatsAppsCache[index].qrcode = data.session.qrcode;
              whatsAppsCache[index].retries = data.session.retries;
            }
          }
        }
      };

      const whatsappEvent = `company-${companyId}-whatsapp`;
      const sessionEvent = `company-${companyId}-whatsappSession`;

      socket.on(whatsappEvent, onCompanyWhatsapp);
      socket.on(sessionEvent, onCompanyWhatsappSession);

      return () => {
        if (socket && typeof socket.off === 'function') {
          socket.off(whatsappEvent, onCompanyWhatsapp);
          socket.off(sessionEvent, onCompanyWhatsappSession);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user?.companyId]); // Dependências corretas

  return { whatsApps, loading };
};

export default useWhatsApps;